// Her room, the picture layer (D_Desk `room`): the still of her room, her working loop on top, and the full-frame
// reactions: she looks up at you (rglance) or nods (rnod), then back to the loop. Day or night follows the user's clock.
// Loading: the still and the loop for the current time only; the two reaction clips are fetched once the loop plays
// (or a few seconds after load), so a reaction starts from the cache. Video plays only while the page is visible;
// with reduced motion there is no video at all (the still, and the reaction's peak frame for its few seconds).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { useReducedMotion } from '../../layout';
import { isDesktopShell } from '../../native';
import { deskAsset, type DeskTime } from './media';
import { REACT_MS, ROOM, PIC } from './roomCopy';
import type { RoomFit } from './roomFit';
import s from './room.module.css';

export type ReactKind = 'glance' | 'nod';
export interface RoomReact { kind: ReactKind; t: number }

/** True while her room can be seen. A browser: the page is visible (Page Visibility). The desktop shells keep the page
 *  "visible" even when the window is hidden or minimized (Electron with backgroundThrottling off), so there the window's
 *  focus stands in: when the user is in another app, the small window shows her instead and the room's video pauses. */
export function useRoomVisible(): boolean {
  const read = () => typeof document === 'undefined' || (document.visibilityState !== 'hidden' && (!isDesktopShell() || document.hasFocus()));
  const [v, setV] = useState(read);
  useEffect(() => {
    const on = () => setV(read());
    document.addEventListener('visibilitychange', on);
    window.addEventListener('focus', on); window.addEventListener('blur', on);
    on();
    return () => { document.removeEventListener('visibilitychange', on); window.removeEventListener('focus', on); window.removeEventListener('blur', on); };
  }, []);
  return v;
}

const prefetched = new Set<string>();
/** Warm the HTTP cache for a clip (low priority), once per URL. */
function prefetch(url: string) {
  if (prefetched.has(url) || typeof fetch === 'undefined') return;
  prefetched.add(url);
  try { void fetch(url, { priority: 'low' } as RequestInit).then((r) => r.blob()).catch(() => prefetched.delete(url)); } catch { prefetched.delete(url); }
}

const muteAll = (v: HTMLVideoElement) => {
  // Autoplay rules check the muted *attribute* on some WebViews; set both.
  v.muted = true; v.defaultMuted = true; v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
};

export function RoomMedia({ ch, time, react, fit, playing }: { ch: CharId; time: DeskTime; react: RoomReact | null; fit: RoomFit; playing: boolean }) {
  const reduced = useReducedMotion();
  const visible = useRoomVisible();
  const motion = !reduced && playing && visible;
  // Day <-> night: the new set fades in over the old one (D_Desk: 1600 ms), then the old one goes.
  const [layers, setLayers] = useState<{ key: string; ch: CharId; time: DeskTime }[]>([{ key: ch + time, ch, time }]);
  useEffect(() => {
    const key = ch + time;
    setLayers((ls) => (ls[ls.length - 1].key === key ? ls : ls.concat([{ key, ch, time }]).slice(-2)));
    const t = setTimeout(() => setLayers((ls) => (ls.length > 1 ? ls.slice(-1) : ls)), 1800);
    return () => clearTimeout(t);
  }, [ch, time]);

  const pw = PIC.w * fit.p;
  const box: JSX.CSSProperties = { position: 'absolute', left: fit.px, top: fit.py, width: pw, height: PIC.h * fit.p, overflow: 'hidden' };
  if (fit.sides) {
    // A window much wider than her picture (roomFit keeps her head and her card in): the picture's edges fade into a
    // soft, dark copy of the room behind it.
    const f = Math.round(Math.min(96, pw * 0.06));
    const l = fit.px > 0.5 ? `transparent 0, #000 ${f}px` : '#000 0';
    const r = fit.px + pw < fit.W - 0.5 ? `#000 calc(100% - ${f}px), transparent 100%` : '#000 100%';
    const mask = `linear-gradient(90deg, ${l}, ${r})`;
    Object.assign(box as Record<string, string | number>, { maskImage: mask, WebkitMaskImage: mask });
  }
  const last = layers[layers.length - 1];
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {fit.sides && <img class={s.sideFill} src={deskAsset(last.ch, last.time, 'room')} alt="" draggable={false} decoding="async" />}
      <div style={box} data-pic data-head={ROOM[ch].head} data-foot={ROOM[ch].by}>
        {layers.map((l, i) => (
          <MediaSet key={l.key} ch={l.ch} time={l.time} react={i === layers.length - 1 ? react : null} motion={motion} fadeIn={i > 0} />
        ))}
      </div>
    </div>
  );
}

function MediaSet({ ch, time, react, motion, fadeIn }: { ch: CharId; time: DeskTime; react: RoomReact | null; motion: boolean; fadeIn: boolean }) {
  const loop = useRef<HTMLVideoElement>(null);
  const [loopOk, setLoopOk] = useState(true);
  const fill: JSX.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', maxWidth: 'none' };
  const glanceUrl = deskAsset(ch, time, 'rglance'), nodUrl = deskAsset(ch, time, 'rnod');

  useEffect(() => {
    const v = loop.current;
    if (!v) return undefined;
    muteAll(v);
    if (motion) { const p = v.play(); if (p) p.catch(() => { /* autoplay blocked: the still stays */ }); } else v.pause();
    return undefined;
  }, [motion, loopOk]);
  // Reaction clips: after the loop is playing (or 4 s), never at boot.
  useEffect(() => {
    if (!motion) return undefined;
    const t = setTimeout(() => { prefetch(glanceUrl); prefetch(nodUrl); }, 4000);
    return () => clearTimeout(t);
  }, [motion, glanceUrl, nodUrl]);

  return (
    <div class={fadeIn ? s.fadeSlow : undefined} style={{ position: 'absolute', inset: 0 }}>
      <img src={deskAsset(ch, time, 'room')} alt="" draggable={false} decoding="async" style={fill} />
      {motion && loopOk && (
        <video
          ref={loop} src={deskAsset(ch, time, 'loop')} poster={deskAsset(ch, time, 'room')} autoplay muted loop playsInline preload="auto"
          disablePictureInPicture disableRemotePlayback
          onPlaying={() => { prefetch(glanceUrl); prefetch(nodUrl); }}
          onError={() => setLoopOk(false)}
          class={s.fadeFast} style={{ ...fill, background: 'transparent' }}
        />
      )}
      {react && <Reaction key={react.t} ch={ch} time={time} kind={react.kind} motion={motion} />}
    </div>
  );
}

/** One reaction: the clip fades in when it plays, out just before it ends; its peak frame when there is no video. */
function Reaction({ ch, time, kind, motion }: { ch: CharId; time: DeskTime; kind: ReactKind; motion: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const started = useRef(false);
  const [on, setOn] = useState(!motion);
  const [off, setOff] = useState(false);
  const [gone, setGone] = useState(false);
  const [still, setStill] = useState(!motion);
  const fill: JSX.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', maxWidth: 'none' };
  const end = REACT_MS(ch);
  const toStill = () => { if (!started.current) { setStill(true); setOn(true); } };
  useEffect(() => {
    const v = ref.current;
    if (v) { muteAll(v); const p = v.play(); if (p) p.catch(toStill); }
    // A clip that hasn't started after 1.2 s (slow network, no codec) gives way to its peak frame.
    const late = setTimeout(toStill, 1200);
    const t1 = setTimeout(() => setOff(true), end);
    const t2 = setTimeout(() => setGone(true), end + 700);
    return () => { clearTimeout(late); clearTimeout(t1); clearTimeout(t2); };
  }, []);
  if (gone) return null;
  const style: JSX.CSSProperties = { ...fill, opacity: on && !off ? 1 : 0, transition: off ? 'opacity 500ms ease' : 'opacity 260ms ease' };
  if (still) return <img src={deskAsset(ch, time, kind === 'glance' ? 'rglance_s' : 'rnod_s')} alt="" draggable={false} style={style} />;
  return (
    <video
      ref={ref} src={deskAsset(ch, time, kind === 'glance' ? 'rglance' : 'rnod')} autoplay muted playsInline preload="auto"
      disablePictureInPicture disableRemotePlayback
      onPlaying={() => { started.current = true; setOn(true); }}
      onError={toStill}
      style={style}
    />
  );
}

/** What the room picture shows, for the accessible label of the room. */
export const roomAlt = (ch: CharId) => ROOM[ch].alt;
